// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Common
import { ModalContent } from '@/common-utils/components/ui/modal';
// React Query
import actionsSession from '@/app/modules/session/api/actionsSession';
// Hooks
import { useToast } from '@/common-utils/hooks/use-toast';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import RenderCgu from '@/common-utils/components/renders/cgu/RenderCgu';
import BodyNews from '@/common-utils/components/renders/modal/components/bodies/BodyNews/BodyNews';
import BodyInitMFA from './BodyInitMFA/BodyInitMFA';
import FooterNews from '@/common-utils/components/renders/modal/components/footers/FooterNews/FooterNews';
// CSS
import './ContentSetMFA.scss';
import { nsSession } from '@/app/i18n/namespaces';

/**
 * @param {function} onClose
 * @returns {JSX}
 */
function ContentSetMFA({ onClose }) {
    // Constants
    const MAX_ATTEMPTS = 3;

    // State
    const [disableValidateButton, setDisableValidateButton] = useState(true);
    const [step, setStep] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [appCode, setAppCode] = useState('');

    // Toast
    const { toast } = useToast();

    // API calls
    const {
        data: qrcodeUri,
        isLoading: isLoadingQrcodUri,
        isFetched,
        refetch: refetchQrcodeUri
    } = actionsSession.getQrcodeUri();

    // Functions
    const closeOnError = () => {
        onClose();
        toast({
            description: <Entity namespace={nsSession} entity='mfa.init_error' />,
            variant: 'danger'
        });
    };

    const errorHandler = () => {
        setAppCode('');
        if (attempts + 1 === MAX_ATTEMPTS) {
            closeOnError();
        }
    };

    // API calls
    const onSuccess = (data) => {
        if (data.data?.success) {
            onClose();
            toast({
                description: <Entity namespace={nsSession} entity='mfa.init_success' />,
                variant: 'success'
            });
        }
        else {
            errorHandler();
        }
    };

    const onError = () => {
        errorHandler();
    };

    const initMfa = actionsSession.initializeMFA(onSuccess, onError);

    useEffect(() => {
        refetchQrcodeUri();
    }, [isFetched]);

    // Functions
    const submitInitializeMfa = () => {
        initMfa.mutate({
            totp: appCode,
            mfaSecret: qrcodeUri.MfaUri.split('secret=')[1].split('&issuer=')[0]
        });
        setAttempts(attempts + 1);
    };

    return (
        <ModalContent className={cn(step === 0 ? 'max-w-5xl' : 'max-w-xl', 'modal-secure-access')}>
            <HeaderTitle title='mfa.modal_title' namespace={nsSession} />
            {step === 0 ?
                <>
                    <BodyNews setDisableValidateButton={setDisableValidateButton}>
                        <RenderCgu />
                    </BodyNews>
                    <FooterNews
                        onValidate={() => setStep(step + 1)}
                        isSubmitDisabled={disableValidateButton}
                    />
                </>
                :
                <>
                    <BodyInitMFA
                        qrcodeUri={qrcodeUri}
                        isLoadingQrcodUri={isLoadingQrcodUri}
                        isLoadingInitMfa={initMfa.isLoading}
                        appCodeSetter={{ appCode, setAppCode }}
                        leftAttempts={MAX_ATTEMPTS - attempts}
                        closeOnError={closeOnError}
                    />
                    <FooterForm
                        onSubmit={submitInitializeMfa}
                        isSubmitDisabled={appCode.length !== 6}
                        isSubmitting={initMfa.isLoading}
                        showSubmitButton={true}
                    />
                </>
            }
        </ModalContent>
    );
}

ContentSetMFA.propTypes = {
    onClose: PropTypes.func
};

export default ContentSetMFA;